import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Dumbbell,
  ArrowLeft,
  Activity,
  ChevronRight,
  User,
} from 'lucide-react';
import { toast } from 'sonner';

const LIFT_COLORS = {
  SQUAT: 'border-blue-500/30 bg-blue-500/10 text-blue-400',
  TRAPBAR: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400',
  BENCH: 'border-amber-500/30 bg-amber-500/10 text-amber-400',
  ROW: 'border-purple-500/30 bg-purple-500/10 text-purple-400',
  HIP_THRUST: 'border-rose-500/30 bg-rose-500/10 text-rose-400',
  CUSTOM: 'border-zinc-500/30 bg-zinc-500/10 text-zinc-400',
};

const AthleteVBTProfilePage = () => {
  const { athleteId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const { t, language } = useLanguage();

  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchProfile = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get(`/vbt/athlete/${athleteId}/profile`);
      setProfile(response.data);
    } catch (err) {
      console.error('Failed to fetch VBT profile:', err);
      toast.error('Failed to load athlete VBT profile');
    } finally {
      setIsLoading(false);
    }
  }, [api, athleteId]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-zinc-500">{t('loading')}</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="text-center py-12">
        <p className="text-zinc-500">{language === 'IT' ? 'Profilo non trovato' : 'Profile not found'}</p>
      </div>
    );
  }

  const { athlete, latest_profiles = {}, test_history = {}, total_tests = 0 } = profile;

  // Map backend response to frontend display format
  const latest_tests = {};
  for (const [lt, data] of Object.entries(latest_profiles)) {
    latest_tests[lt] = { ...data, id: data.test_id };
  }

  const all_tests = [];
  for (const [lt, tests] of Object.entries(test_history)) {
    for (const t of tests) {
      all_tests.push({
        id: t.test_id,
        lift_type: lt,
        date: t.date,
        data_points: t.data_points,
        lv_profile: t.r_squared != null ? { l0: t.l0, v0: t.v0, r_squared: t.r_squared } : null,
      });
    }
  }

  const lift_types_tested = Object.keys(test_history);

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/staff/vbt')}
          className="text-zinc-400 hover:text-zinc-100"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-100 flex items-center gap-2">
            <User className="w-6 h-6 text-amber-500" />
            {athlete.name}
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {athlete.position} • {language === 'IT' ? 'Profilo VBT' : 'VBT Profile'}
          </p>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4 text-center">
            <Activity className="w-5 h-5 text-blue-400 mx-auto mb-2" />
            <div className="text-2xl font-bold text-zinc-100" data-testid="stat-total-tests">{total_tests}</div>
            <div className="text-xs text-zinc-500 uppercase">
              {language === 'IT' ? 'Test Totali' : 'Total Tests'}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4 text-center">
            <Dumbbell className="w-5 h-5 text-emerald-400 mx-auto mb-2" />
            <div className="text-2xl font-bold text-zinc-100" data-testid="stat-lifts-tested">{lift_types_tested.length}</div>
            <div className="text-xs text-zinc-500 uppercase">
              {language === 'IT' ? 'Esercizi Testati' : 'Lifts Tested'}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Latest Tests by Lift Type */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-zinc-100">
            {language === 'IT' ? 'Profili Load-Velocity Attuali' : 'Current Load-Velocity Profiles'}
          </CardTitle>
          <CardDescription className="text-zinc-500">
            {language === 'IT' ? 'Ultimo test per ogni esercizio' : 'Latest test for each lift'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {Object.keys(latest_tests).length === 0 ? (
            <div className="text-center py-8 text-zinc-500">
              {language === 'IT' ? 'Nessun test registrato' : 'No tests recorded'}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(latest_tests).map(([liftType, test]) => (
                <Link
                  key={liftType}
                  to={`/staff/vbt/tests/${test.id}`}
                  className={`block p-4 rounded-sm border transition-colors hover:bg-zinc-800/30 ${LIFT_COLORS[liftType] || LIFT_COLORS.CUSTOM}`}
                  data-testid={`latest-profile-${liftType}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-semibold">{liftType}</span>
                    <span className="text-xs text-zinc-500">{test.date}</span>
                  </div>
                  {test.lv_profile ? (
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-zinc-500">L0 (1RM)</span>
                        <span className="text-lg font-bold text-zinc-100">{typeof test.lv_profile.l0 === 'number' ? test.lv_profile.l0.toFixed(1) : test.lv_profile.l0} kg</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-zinc-500">V0</span>
                        <span className="text-sm text-zinc-300">{typeof test.lv_profile.v0 === 'number' ? test.lv_profile.v0.toFixed(1) : test.lv_profile.v0} m/s</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-zinc-500">R²</span>
                        <span className={`text-sm font-medium ${test.lv_profile.r_squared >= 0.9 ? 'text-emerald-400' : 'text-amber-400'}`}>
                          {typeof test.lv_profile.r_squared === 'number' ? test.lv_profile.r_squared.toFixed(2) : test.lv_profile.r_squared}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-zinc-500">
                      {language === 'IT' ? 'Profilo non calcolato' : 'Profile not calculated'}
                    </div>
                  )}
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* All Tests History */}
      {all_tests.length > 0 && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-zinc-100">
              {language === 'IT' ? 'Storico Test' : 'Test History'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {all_tests.map((test) => (
                <Link
                  key={test.id}
                  to={`/staff/vbt/tests/${test.id}`}
                  className="flex items-center justify-between p-3 bg-zinc-950/50 rounded-sm border border-zinc-800 hover:border-zinc-700 transition-colors"
                  data-testid={`test-history-${test.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${
                      LIFT_COLORS[test.lift_type]?.includes('blue') ? 'bg-blue-400' :
                      LIFT_COLORS[test.lift_type]?.includes('emerald') ? 'bg-emerald-400' :
                      LIFT_COLORS[test.lift_type]?.includes('amber') ? 'bg-amber-400' :
                      LIFT_COLORS[test.lift_type]?.includes('purple') ? 'bg-purple-400' :
                      LIFT_COLORS[test.lift_type]?.includes('rose') ? 'bg-rose-400' :
                      'bg-zinc-400'
                    }`} />
                    <div>
                      <span className="font-medium text-zinc-100">{test.lift_type}</span>
                      <span className="text-xs text-zinc-500 ml-2">{test.data_points} pts</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {test.lv_profile && (
                      <span className="text-sm text-zinc-400">
                        L0: <span className="text-zinc-100 font-medium">{typeof test.lv_profile.l0 === 'number' ? test.lv_profile.l0.toFixed(1) : test.lv_profile.l0}kg</span>
                      </span>
                    )}
                    <span className="text-xs text-zinc-500">{test.date}</span>
                    <ChevronRight className="w-4 h-4 text-zinc-600" />
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AthleteVBTProfilePage;
